import type { ReactNode } from 'react'
import { useAuth } from '@auth/AuthContext.tsx'
import { AppShell } from '@components/AppShell.tsx'
import { APPS, type AppSlug } from '@config'
import { ActivitiesApp } from '@apps/activities/Activities.tsx'
import { GroceriesApp } from '@apps/groceries/Groceries.tsx'
import { MealsApp } from '@apps/meals/Meals.tsx'
import { RecipesApp } from '@apps/recipes/Recipes.tsx'
import { TodoApp } from '@apps/todo/Todo.tsx'
import { AppStub } from '@pages/AppStub.tsx'
import { Landing } from '@pages/Landing.tsx'
import { NotFound } from '@pages/NotFound.tsx'
import { Onboarding } from '@pages/Onboarding.tsx'
import { Settings } from '@pages/settings/Settings.tsx'
import { SignIn } from '@pages/SignIn.tsx'
import { useRouter } from '@router'
import styles from './App.module.css'
import useDocTitle from '@hooks/useTitle'
import capitalize from './utils/capitalize'

type Slug = 'todo' | 'activities' | 'meals' | 'recipes' | 'groceries'

type RouteDetails =
	| {
			kind: 'app'
			slug: Slug
	  }
	| {
			kind: 'settings'
			slug?: never
	  }
	| {
			kind: 'landing'
			slug?: never
	  }
	| {
			kind: 'notfound'
			slug?: never
	  }

// type RouteDetails = {
//     readonly kind: "landing";
//     readonly slug?: never;
// 	readonly title?: never;
// } | {
//     readonly kind: "app";
//     readonly slug: ;
// 	readonly title: 'Todo' | 'Activities' | 'Meals' | 'Recipes'
// } | {
//     readonly kind: "notfound";
//     readonly slug?: never;
// }

const matchRoute = (path: string): RouteDetails => {
	if (path === '/' || path === '') return { kind: 'landing' } as const
	const seg = path.replace(/^\//, '').split('/')[0]
	// Ahead of the APPS lookup, and deliberately not *in* APPS — that list also
	// drives the landing grid, the AppStub fallback and the synchronous favicon
	// script in index.html.
	if (seg === 'settings') return { kind: 'settings' } as const
	const app = APPS.find((a) => a.slug === seg)
	if (app) return { kind: 'app', slug: app.slug as AppSlug } as const
	return { kind: 'notfound' } as const
}

export const App = () => {
	const { state } = useAuth()
	const { path } = useRouter()
	const route = matchRoute(path)
	useDocTitle(capitalize(route.kind === 'settings' ? 'settings' : route.slug))

	if (state.status === 'loading') {
		return (
			<div className={styles.splash}>
				<img src="/logo.png" alt="" className={styles.mark} />
			</div>
		)
	}

	if (state.status === 'anonymous') return <SignIn />

	if (state.user.displayName === null) {
		return <Onboarding user={state.user} household={state.household} />
	}

	// Persistent app shell (scrolling content + bottom tab bar + FAB slot)
	// wraps the signed-in surface (Home + every mini-app, including stubs).
	// Sign-in / onboarding / NotFound stay bare.
	const shell = (page: ReactNode) => <AppShell>{page}</AppShell>

	if (route.kind === 'landing') return shell(<Landing />)
	if (route.kind === 'settings') return shell(<Settings />)
	if (route.kind === 'app') {
		if (route.slug === 'todo') return shell(<TodoApp />)
		if (route.slug === 'meals') return shell(<MealsApp />)
		if (route.slug === 'recipes') return shell(<RecipesApp />)
		if (route.slug === 'activities') return shell(<ActivitiesApp />)
		if (route.slug === 'groceries') return shell(<GroceriesApp />)
		return shell(<AppStub slug={route.slug} />)
	}
	return <NotFound />
}
